import { debounce } from '@thistle/utils'

export interface User {
  identityProvider: 'mainframe' | 'google';
  name: string;
  email?: string;
  id?: string;
  picture?: string;
  verified_email?: boolean;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  idToken: string | null;
}

class AuthService {
  private renewalTimer?: number;
  private authState: AuthState;
  private tokenIssuerUrl?: string;

  configure(tokenIssuerUrl: string) {
    this.tokenIssuerUrl = tokenIssuerUrl;
  }

  constructor() {
    const storedAuth = window.localStorage.getItem('thistle_auth_state');
    if (storedAuth) {
      try {
        this.authState = JSON.parse(storedAuth);
        return;
      } catch(error) {
        console.error('Error loading stored auth state:', error);
        this.authState = {isAuthenticated: false, user: null, idToken: null};
      }
    } else {
        this.authState = {isAuthenticated: false, user: null, idToken: null};
    }
  }

  private emitLoggedInEvent() {
    document.dispatchEvent(new CustomEvent("thistle-signed-in"));
  }

  private emitLoggedOutEvent() {
    document.dispatchEvent(new CustomEvent("thistle-signed-out"));
  }

  private handleEvent(oldAuthenticatedState: boolean, newAuthenticatedState: boolean) {
    if (oldAuthenticatedState === newAuthenticatedState) {
      return;
    }
    if (newAuthenticatedState) {
      this.emitLoggedInEvent();
    } else {
      this.emitLoggedOutEvent();
    }
  }

  private getIssuerURL(): string | null {
    return this.tokenIssuerUrl ?? null;
  }

  private async tryFacilityAuth(): Promise<string | null> {
    if (!this.getIssuerURL()) {
      console.log("No mainframe token issuer URL configured, skipping facility auth");
      return null;
    }

    try {
      console.log("Attempting facility authentication");
      const response = await fetch(`${this.getIssuerURL()}/issue-token`, {
        method: 'POST'
      });

      if (!response.ok) {
        console.log(`Facility auth failed with status: ${response.status}`);
        return null;
      }

      const data = await response.json();
      if (data.token) {
        console.log("Facility authentication successful");
        return data.token;
      } else {
        console.log("Facility auth response missing token");
        return null;
      }
    } catch (error) {
      console.log("Facility authentication failed:", error);
      return null;
    }
  }

  private saveAuthToLocalStorage() {
    window.localStorage.setItem('thistle_auth_state', JSON.stringify(this.authState));
  }

  private clearAuthState() {
    this.setAuthState(false, null, null);
  }

  private setAuthState(isAuthenticated: boolean, user: User | null, idToken: string | null) {
    const prevAuthenticationState = this.authState.isAuthenticated;
    this.authState = {
      isAuthenticated: isAuthenticated,
      user: user,
      idToken: idToken
    }
    this.saveAuthToLocalStorage();
    this.handleEvent(prevAuthenticationState, isAuthenticated);
  }

  private handleFacilityAuthToken(token: string): boolean {
    try {
      const payload = this.decodeJwt(token);
      if (!payload) {
        console.error("Failed to decode facility JWT payload");
        return false;
      }

      const user: User = {
        identityProvider: 'mainframe',
        name: payload.name,
      };

      this.setAuthState(true, user, token);
      return true;
    } catch (error) {
      console.error("Error parsing facility token:", error);
      return false;
    }
  }

  // GOOGLE AUTH

  handleGoogleCredentialResponse = (response: any) => {
    console.log("Saving fresh credentials from Google");
    if (response.credential) {
      try {
        const payload = this.decodeJwt(response.credential);
        if (!payload) {
          console.error("Failed to decode JWT payload");
          return;
        }

        const user: User = {
          identityProvider: 'google',
          id: payload.sub,
          email: payload.email,
          name: payload.name,
          picture: payload.picture,
          verified_email: payload.email_verified,
        };

        // Update auth state in the main store
        this.setAuthState(true, user, response.credential);
      } catch (error) {
        console.error("Error parsing credential response:", error);
      }
    }
  };

  // debounce so we don't send lots of requests to the Google SDK. It might be harmless,
  // but let's not chance it.
  private googlePrompt = debounce((): void => {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.prompt();
    } else {
      // wait for Google SDK to load
      setTimeout(this.googlePrompt, 300);
    }
  }, 200);

  // debouncing/waiting here same as for prompt()
  private googlePromptCancel = debounce((): void => {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.cancel();
    } else {
      setTimeout(this.googlePromptCancel, 300);
    }
  }, 100);

  // SHARED FUNCTIONS

  private decodeJwt(token: string): any {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      return JSON.parse(window.atob(base64));
    } catch (error) {
      console.error("Error decoding JWT:", error);
      return null;
    }
  }

  private isTokenExpired(token: string): boolean {
    return this.isTokenExpiringSoon(token, 0);
  }

  private isTokenExpiringSoon(token: string, bufferMinutes: number = 5): boolean {
    const payload = this.decodeJwt(token);
    if (!payload || !payload.exp) {
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const bufferSeconds = bufferMinutes * 60;
    return payload.exp <= currentTime + bufferSeconds;
  }

  private async attemptAuthentication(): Promise<void> {
    // If there's an active Google session, stick with Google; don't try facility auth
    if (this.isGoogleAuthenticated()) {
      this.googlePrompt();
      return
    }

    // First try facility authentication
    const facilityToken = await this.tryFacilityAuth();
    if (facilityToken) {
      if (this.handleFacilityAuthToken(facilityToken)) {
        // If facility auth token is good, don't show Google prompt
        this.googlePromptCancel();
        return;
      }
    }

    // Fall back to Google authentication
    this.googlePrompt();
  }

  private startTokenRenewalTimer(): void {
    // Clear any existing timer
    this.removeRenewalTimer()

    // Check token status every 60 seconds
    this.renewalTimer = window.setInterval(() => {
      this.checkAndRenewToken();
    }, 60000);
  }

  private async checkAndRenewToken(): Promise<void> {
    const currentToken = this.authState.idToken;

    if (!currentToken) {
      return; // No token to check
    }

    // If token is expiring soon (within 5 minutes), try to renew
    if (this.isTokenExpiringSoon(currentToken, 5)) {
      console.log("Token expiring soon, attempting renewal");
      await this.attemptAuthentication();
    }
  }

  // Public API

  async getValidIdToken(): Promise<string | null> {
    // Re-attempt to sign in if doing facility auth, otherwise show Google sign in prompt
    if (!this.authState.idToken || this.isTokenExpired(this.authState.idToken)) {
      this.signOut();
      await this.attemptAuthentication();
    }
    return this.authState.idToken;
  }

  async initializeAuth(): Promise<void> {
    if (!this.isAuthenticated()) {
      await this.attemptAuthentication();
    }

    if (this.isAuthenticated()) {
      this.googlePromptCancel();
    }

    // Start periodic token renewal check
    this.startTokenRenewalTimer();

    // Make the callback available globally
    window.handleGoogleSignIn = authService.handleGoogleCredentialResponse;
  }

  removeRenewalTimer(): void {
    // Clear renewal timer
    if (this.renewalTimer) {
      clearInterval(this.renewalTimer);
      this.renewalTimer = undefined;
    }
  }

  signOut(): void {
    this.removeRenewalTimer();
    this.clearAuthState();
  }

  isAuthenticated(): boolean {
    return this.authState.isAuthenticated;
  }

  isGoogleAuthenticated(): boolean {
    return this.authState.user?.identityProvider == "google";
  }

  getUser(): User | null {
    return this.authState.user;
  }
}

export const authService = new AuthService();

// Add global callback function for Google Identity Services
declare global {
  interface Window {
    handleGoogleSignIn: (response: any) => void;
    google: any;
  }
}
